import { ChevronLeftIcon, CloseIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import React from 'react'
import {
	BackLink,
	BarActions,
	BarMeta,
	BarSummary,
	BarTitle,
	BottomBar as BottomBarRow,
	CardContent,
	CtaButton,
	FlowCard,
	FlowContentArea,
	HeaderBackButton,
	HeaderCloseButton,
	HeaderRow,
	HeaderSlot,
	HeaderTitle,
	StepBar,
	StepIndicator,
	StepLabel,
	StepTab,
} from './CreateTripPage.styles'
import { BottomBar, StepIndicatorState, canJumpTo } from './core/presentation'

export interface FlowShellStep<S extends string> {
	key: S
	label: string
	state: StepIndicatorState
}

interface FlowShellProps<S extends string> {
	/** The header's centre: "New Trip" / "Edit Destinations". */
	title: string
	showsHeaderBack: boolean
	onBack: () => void
	onClose: () => void
	steps: FlowShellStep<S>[]
	onSelectStep: (step: S) => void
	bar: BottomBar
	/** Disables the CTA while its request is in flight. */
	ctaPending?: boolean
	onCta: () => void
	children: React.ReactNode
}

/**
 * The chrome the create-trip and edit-destinations flows share, like iOS's
 * shared header, progress bar and bottom bar views: the header row (back slot
 * · title · close), the card with its labelled step indicator, the step's
 * content, and the bottom bar summarising the draft with the step's CTA.
 */
const FlowShell = <S extends string>({
	title,
	showsHeaderBack,
	onBack,
	onClose,
	steps,
	onSelectStep,
	bar,
	ctaPending = false,
	onCta,
	children,
}: FlowShellProps<S>): React.ReactElement => (
	<FlowContentArea>
		<HeaderRow>
			<HeaderSlot>
				{showsHeaderBack && (
					<HeaderBackButton type="button" aria-label="Back" onClick={onBack}>
						<ChevronLeftIcon sx={{ fontSize: iconSizes.xl }} />
					</HeaderBackButton>
				)}
			</HeaderSlot>
			<HeaderTitle>{title}</HeaderTitle>
			<HeaderCloseButton type="button" aria-label="Cancel" onClick={onClose}>
				<CloseIcon sx={{ fontSize: iconSizes.lg }} />
			</HeaderCloseButton>
		</HeaderRow>

		<FlowCard>
			<StepIndicator>
				{steps.map(step => (
					<StepTab
						key={step.key}
						type="button"
						$state={step.state}
						disabled={!canJumpTo(step.state)}
						aria-current={step.state === 'active' ? 'step' : undefined}
						onClick={() => onSelectStep(step.key)}
					>
						<StepBar $state={step.state} />
						<StepLabel $state={step.state}>{step.label}</StepLabel>
					</StepTab>
				))}
			</StepIndicator>

			<CardContent>{children}</CardContent>

			<BottomBarRow>
				<BarSummary>
					<BarTitle>{bar.title}</BarTitle>
					<BarMeta>{bar.subtitle}</BarMeta>
				</BarSummary>
				<BarActions>
					{bar.showsBack && (
						<BackLink type="button" onClick={onBack}>
							Back
						</BackLink>
					)}
					<CtaButton
						type="button"
						$variant={bar.ctaStyle}
						disabled={!bar.ctaEnabled || ctaPending}
						onClick={onCta}
					>
						{bar.ctaTitle}
					</CtaButton>
				</BarActions>
			</BottomBarRow>
		</FlowCard>
	</FlowContentArea>
)

export default FlowShell
